import { useRef, useState, useCallback } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { BEFORE_AFTER } from "../data/content";

export const CompareSlider = ({ pair }) => {
    const ref = useRef(null);
    const dragging = useRef(false);
    const [pos, setPos] = useState(50);

    const update = useCallback((clientX) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        setPos(Math.min(94, Math.max(6, ((clientX - rect.left) / rect.width) * 100)));
    }, []);

    const onPointerDown = (e) => {
        dragging.current = true;
        ref.current.setPointerCapture(e.pointerId);
        update(e.clientX);
    };
    const onPointerMove = (e) => dragging.current && update(e.clientX);
    const stop = () => (dragging.current = false);

    const onKeyDown = (e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(6, p - 4));
        if (e.key === "ArrowRight") setPos((p) => Math.min(94, p + 4));
    };

    return (
        <div
            ref={ref}
            data-testid="interactive-before-after-slider"
            className="relative aspect-[4/3] sm:aspect-[3/2] w-full overflow-hidden rounded-3xl select-none cursor-ew-resize touch-none shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)]"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={stop}
            onPointerCancel={stop}
        >
            <img src={pair.before} alt={`Vorher: ${pair.labelBefore}`} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
                <img src={pair.after} alt={`Nachher: ${pair.labelAfter}`} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            </div>

            <span className="absolute top-4 left-4 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] bg-forest-deep/70 backdrop-blur text-white/90 px-3 py-1.5 rounded-full">
                Vorher · {pair.labelBefore}
            </span>
            <span className="absolute top-4 right-4 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] bg-leaf/85 backdrop-blur text-white px-3 py-1.5 rounded-full">
                Nachher · {pair.labelAfter}
            </span>

            <div className="absolute top-0 bottom-0 w-px bg-white/80" style={{ left: `${pos}%` }}>
                <button
                    data-testid="before-after-handle"
                    aria-label="Vergleichsregler verschieben"
                    role="slider"
                    aria-valuenow={Math.round(pos)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    onKeyDown={onKeyDown}
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white text-forest-deep flex items-center justify-center shadow-[0_8px_30px_rgba(0,0,0,0.35)] cursor-ew-resize focus:outline-none focus:ring-2 focus:ring-leaf"
                >
                    <ChevronsLeftRight size={20} strokeWidth={2.4} />
                </button>
            </div>
        </div>
    );
};

export const BeforeAfter = () => {
    const [active, setActive] = useState(0);
    const pair = BEFORE_AFTER[active];

    return (
        <section id="vorher-nachher" className="relative bg-forest-deep grain py-20 lg:py-32 overflow-hidden" data-testid="before-after-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
                <Reveal>
                    <Eyebrow num="02" label="Vorher / Nachher" dark />
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                        <h2 className="font-display font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.12] max-w-2xl">
                            Der Unterschied,<br />den man sofort sieht.
                        </h2>
                        <p className="text-base sm:text-lg text-white/60 leading-relaxed max-w-md">
                            Ziehen Sie den Regler und sehen Sie selbst, was professionelle
                            Reinigung und Pflege bewirkt.
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="mt-10 flex flex-wrap gap-3">
                        {BEFORE_AFTER.map((p, i) => (
                            <button
                                key={p.id}
                                data-testid={`before-after-tab-${p.id}`}
                                onClick={() => setActive(i)}
                                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-[background-color,color,border-color] duration-200 border ${
                                    active === i
                                        ? "bg-white text-forest-deep border-white"
                                        : "bg-transparent text-white/60 border-white/20 hover:border-white/50 hover:text-white"
                                }`}
                            >
                                {p.title}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.15} className="mt-8">
                    <CompareSlider key={pair.id} pair={pair} />
                    <p className="mt-5 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">
                        {pair.caption}
                    </p>
                </Reveal>
            </div>
        </section>
    );
};
